(function () {
  'use strict';

  const PurchaseMode = {
    ONE_TIME: 'one-time',
    SUBSCRIPTION: 'subscription',
  };

  const SEL = {
    root: '[data-nw-tab-variants-root]',
    tabBtn: '[data-tab]',
    radioChecked:
      '.nw-tab-variants-item-variants-item input[type="radio"]:checked',
    radioAny: '.nw-tab-variants-item-variants-item input[type="radio"]',
    variantRow: '[data-variant-row]',
    purchaseInput: '[data-nw-purchase-input]',
    frequencySection: '[data-nw-frequency-section]',
    frequencySelect: '[data-nw-frequency-select]',
    frequencyBenefits: '[data-nw-frequency-benefits]',
  };

  function findAddToCartForm(root) {
    const productInfo = root.closest('product-info');
    const container = root.closest('.product__info-container') || productInfo;
    if (!container) return null;
    const productForm = container.querySelector('product-form.product-form');
    if (!productForm) return null;
    return (
      productForm.querySelector('form[data-type="add-to-cart-form"]') ||
      productForm.querySelector('form[action*="cart/add"]') ||
      null
    );
  }

  function ensureSellingPlanInput(form) {
    let el = form.querySelector('input[name="selling_plan"]');
    if (!el) {
      el = document.createElement('input');
      el.type = 'hidden';
      el.name = 'selling_plan';
      form.appendChild(el);
    }
    return el;
  }

  function formatMoney(cents, moneyFormat) {
    if (cents == null || Number.isNaN(Number(cents))) return '';
    const format = moneyFormat || '${{amount}}';
    const n = Math.max(0, Math.round(Number(cents)));
    const value = (n / 100).toFixed(2);
    const valueNoDecimals = String(Math.max(0, Math.round(n / 100)));
    return format
      .replace(/\{\{\s*amount_no_decimals\s*\}\}/gi, valueNoDecimals)
      .replace(/\{\{\s*amount\s*\}\}/gi, value);
  }

  function getSaveState(mode, variant, plan) {
    if (mode === PurchaseMode.ONE_TIME) {
      if (!variant.compare_at_price || variant.compare_at_price <= variant.price) {
        return { show: false, text: '' };
      }
      const pct = Math.round(
        ((variant.compare_at_price - variant.price) / variant.compare_at_price) * 100
      );
      return { show: pct > 0, text: `Save ${pct}%` };
    }
    if (!plan) return { show: false, text: '' };
    const subPrice = plan.price;
    const retail = variant.compare_at_price ? variant.compare_at_price : variant.price;
    if (retail > subPrice) {
      const pct = Math.round(((retail - subPrice) / retail) * 100);
      return { show: pct > 0, text: `Save ${pct}%` };
    }
    return { show: false, text: '' };
  }

  function inferMonthIntervalFromVariantTitle(title) {
    if (!title || typeof title !== 'string') return null;
    const t = title.toLowerCase();
    if (/\bsingle\b/.test(t)) return 1;
    if (/\btriple\b/.test(t)) return 3;
    if (/\bsix\b/.test(t)) return 6;
    if (/\bnine\b/.test(t)) return 9;
    return null;
  }

  function extractMonthsIntervalFromPlanName(planName) {
    if (!planName || typeof planName !== 'string') return null;
    const s = planName.trim().toLowerCase();
    if (/\bmonthly\b|\bevery\s+month\b/.test(s)) return 1;
    const digit = s.match(/\b(\d{1,2})\s*[–\-/]?\s*(?:months?|mos\b)/);
    if (digit) return parseInt(digit[1], 10);
    return null;
  }

  function findSellingPlanIndexForMonths(plans, targetMonths) {
    if (!plans?.length || targetMonths == null || Number.isNaN(Number(targetMonths))) return -1;
    const target = Number(targetMonths);
    for (let i = 0; i < plans.length; i += 1) {
      if (extractMonthsIntervalFromPlanName(plans[i].name) === target) return i;
    }
    const wordRe = new RegExp(
      `(?:^|[^0-9])${target}\\s*[–\\-/.]?\\s*(?:months?|mos\\b)`,
      'i'
    );
    for (let i = 0; i < plans.length; i += 1) {
      if (wordRe.test(plans[i].name)) return i;
    }
    return -1;
  }

  function selectRadioBySlotInPanel(radios, slot1Based) {
    if (!radios?.length) return null;
    const n = Number(slot1Based);
    const slot = Number.isFinite(n) && n >= 1 ? n : 1;
    const idx = Math.min(slot, radios.length) - 1;
    return radios[idx] || null;
  }

  const Utils = {
    variantInPanel(cfg, variantId, panelKey) {
      const v = cfg.variants[String(variantId)];
      if (!v || panelKey === 'all') return true;
      if (cfg.useDogSizeMetafield) {
        if (panelKey === 'small') return v.dogSize === cfg.dogSizeSmall;
        if (panelKey === 'medium') return v.dogSize === cfg.dogSizeMedium;
        if (panelKey === 'large') return v.dogSize === cfg.dogSizeLarge;
        return false;
      }
      const title = (v.title || '').toLowerCase();
      const largeMarker = cfg.largeMarkerLower || '';
      const mediumMarker = cfg.mediumMarkerLower || '';
      if (panelKey === 'large') return Boolean(largeMarker) && title.includes(largeMarker);
      if (panelKey === 'medium') return Boolean(mediumMarker) && title.includes(mediumMarker);
      if (panelKey === 'small') {
        const hasLarge = Boolean(largeMarker) && title.includes(largeMarker);
        const hasMedium = Boolean(mediumMarker) && title.includes(mediumMarker);
        return !hasLarge && !hasMedium;
      }
      return false;
    },

    resolvePanelForVariant(cfg, variantId) {
      if (Utils.variantInPanel(cfg, variantId, 'large')) return 'large';
      if (Utils.variantInPanel(cfg, variantId, 'medium')) return 'medium';
      return 'small';
    },
  };

  class NwTabVariants {
    constructor(root) {
      this.root = root;
      this._config = null;
      this._form = null;
      this._idInput = null;
      this._sellingPlanInput = null;
      this._frequencySection = null;
      this._frequencySelect = null;
      this._onIdInputChange = this._onIdInputChange.bind(this);
      this._onRadioChange = this._onRadioChange.bind(this);
      this._handleTabClick = this._handleTabClick.bind(this);
      this._handleTabKeyboard = this._handleTabKeyboard.bind(this);
      this._onPurchaseTypeChange = this._onPurchaseTypeChange.bind(this);
      this._onFrequencyChange = this._onFrequencyChange.bind(this);
      this._isInit = false;
    }

    init() {
      if (this._isInit) return;
      const sc = this.root.querySelector('script[data-nw-tab-variants-config]');
      if (!sc || !sc.textContent.trim()) return;
      try {
        this._config = JSON.parse(sc.textContent);
      } catch (e) {
        console.warn('[NwTabVariants] Invalid config JSON', e);
        return;
      }

      this._form = findAddToCartForm(this.root);
      this._idInput = this._form?.querySelector('input[name="id"]');
      if (this._form) {
        this._sellingPlanInput = ensureSellingPlanInput(this._form);
      }

      this._frequencySection = this.root.querySelector(SEL.frequencySection);
      this._frequencySelect = this.root.querySelector(SEL.frequencySelect);

      if (this._idInput) {
        this._idInput.addEventListener('change', this._onIdInputChange);
      }

      this.root.querySelectorAll(SEL.radioAny).forEach((inp) => {
        inp.addEventListener('change', this._onRadioChange);
      });

      this.root.querySelectorAll(SEL.tabBtn).forEach((btn) => {
        btn.addEventListener('keydown', this._handleTabKeyboard);
        btn.addEventListener('click', this._handleTabClick);
      });

      this.root.querySelectorAll(SEL.purchaseInput).forEach((inp) => {
        inp.addEventListener('change', this._onPurchaseTypeChange);
      });

      if (this._frequencySelect) {
        this._frequencySelect.addEventListener('change', this._onFrequencyChange);
      }

      if (this._usesSubscription()) {
        const mode = this._config.defaultPurchaseType || PurchaseMode.ONE_TIME;
        this.root.setAttribute('data-purchase-type', mode);
      }

      this._maybeAlignPanelsFromSelection();
      this._syncSubscription();
      this._pushCheckedVariantToForm();
      this._refreshSelectionClasses();
      this._isInit = true;
    }

    _usesSubscription() {
      return Boolean(this._config?.showPurchaseTabs && this._config?.subscriptionEnabled);
    }

    _getVariantData(variantId) {
      return this._config?.variants?.[String(variantId)] || null;
    }

    _getSelectedVariantId() {
      const checked = this.root.querySelector(SEL.radioChecked);
      return checked ? checked.value : null;
    }

    _getPurchaseType() {
      const c = this.root.querySelector(`${SEL.purchaseInput}:checked`);
      if (c) return c.value;
      return this.root.getAttribute('data-purchase-type') || PurchaseMode.ONE_TIME;
    }

    _hasSellingPlansForVariantId(variantId) {
      const v = this._getVariantData(variantId);
      return Boolean(v?.selling_plans?.length);
    }

    _getSelectedPlan() {
      const variantId = this._getSelectedVariantId();
      const v = this._getVariantData(variantId);
      if (!v?.selling_plans?.length) return null;
      const id = this._frequencySelect?.value || String(v.selling_plans[0].id);
      return v.selling_plans.find((p) => String(p.id) === String(id)) || v.selling_plans[0];
    }

    _setSubscriptionOptionDisabled(disabled) {
      const sub = this.root.querySelector(`${SEL.purchaseInput}[value="subscription"]`);
      if (!sub) return;
      sub.disabled = disabled;
      sub.parentElement?.classList.toggle('is-disabled', disabled);
    }

    _rebuildFrequencyOptions(variantId) {
      if (!this._frequencySelect) return;
      const v = this._getVariantData(variantId);
      this._frequencySelect.innerHTML = '';
      if (!v?.selling_plans?.length) return;

      let title = v.title || '';
      const esc = String(variantId).replace(/\\/g, '\\\\').replace(/"/g, '\\"');
      const radio = this.root.querySelector(`${SEL.radioAny}[value="${esc}"]`);
      if (radio?.getAttribute('data-title')) title = radio.getAttribute('data-title');

      const monthHint = inferMonthIntervalFromVariantTitle(title);
      let recommendedIndex = 0;
      if (monthHint != null) {
        const idx = findSellingPlanIndexForMonths(v.selling_plans, monthHint);
        if (idx >= 0) recommendedIndex = idx;
      }

      v.selling_plans.forEach((plan, i) => {
        const opt = document.createElement('option');
        opt.value = String(plan.id);
        const rec = i === recommendedIndex ? ' (recommended)' : '';
        opt.textContent = `${plan.name}${rec}`;
        this._frequencySelect.appendChild(opt);
      });

      this._frequencySelect.selectedIndex = recommendedIndex;
    }

    _updateFrequencyVisibility() {
      if (!this._frequencySection) return;
      const mode = this._getPurchaseType();
      const variantId = this._getSelectedVariantId();
      const show =
        this._usesSubscription() &&
        mode === PurchaseMode.SUBSCRIPTION &&
        this._hasSellingPlansForVariantId(variantId);
      this._frequencySection.hidden = !show;
      this._frequencySection.setAttribute('aria-hidden', show ? 'false' : 'true');
    }

    _updateFrequencyBenefits() {
      const el = this.root.querySelector(SEL.frequencyBenefits);
      if (!el) return;
      const variantId = this._getSelectedVariantId();
      const v = this._getVariantData(variantId);
      const html = v?.subscriptionBenefits || this._config?.defaultFrequencyBenefits || '';
      if (html) {
        el.innerHTML = html;
        el.hidden = false;
      } else {
        el.innerHTML = '';
        el.hidden = true;
      }
    }

    _updateDynamicPrices() {
      if (!this._usesSubscription()) return;
      const mode = this._getPurchaseType();
      const selectedId = this._getSelectedVariantId();
      const mf = this._config.moneyFormat;

      this.root.querySelectorAll(SEL.variantRow).forEach((row) => {
        const id = row.dataset.variantId;
        const v = this._getVariantData(id);
        if (!v) return;

        const currentEl = row.querySelector('[data-nw-price-current]');
        const compareEl = row.querySelector('[data-nw-price-compare]');
        const saveWrap = row.querySelector('[data-nw-save-wrap]');
        const saveText = row.querySelector('[data-nw-save-text]');
        if (!currentEl) return;

        let showCompare = false;
        let currentCents;
        let compareCents = null;

        if (mode === PurchaseMode.ONE_TIME) {
          currentCents = v.price;
          if (v.compare_at_price && v.compare_at_price > v.price) {
            compareCents = v.compare_at_price;
            showCompare = true;
          }
        } else {
          const plan =
            id === selectedId && v.selling_plans?.length
              ? this._getSelectedPlan()
              : v.selling_plans?.[0] || null;
          currentCents = plan ? plan.price : v.price;
          if (plan?.compare_at_price && plan.compare_at_price > plan.price) {
            compareCents = plan.compare_at_price;
            showCompare = true;
          } else if (!plan && v.compare_at_price && v.compare_at_price > v.price) {
            compareCents = v.compare_at_price;
            showCompare = true;
          } else if (plan && v.compare_at_price) {
            compareCents = v.compare_at_price;
            showCompare = compareCents > currentCents;
          } else if (v.compare_at_price && v.compare_at_price > currentCents) {
            compareCents = v.compare_at_price;
            showCompare = true;
          }
        }

        const planForSave =
          mode === PurchaseMode.SUBSCRIPTION
            ? id === selectedId
              ? this._getSelectedPlan()
              : v.selling_plans?.[0]
            : null;
        const save = getSaveState(mode, v, planForSave);

        currentEl.textContent = formatMoney(currentCents, mf);
        if (compareEl) {
          if (showCompare && compareCents != null) {
            compareEl.textContent = formatMoney(compareCents, mf);
            compareEl.hidden = false;
          } else {
            compareEl.textContent = '';
            compareEl.hidden = true;
          }
        }
        if (saveWrap && saveText) {
          if (save.show) {
            saveText.textContent = save.text;
            saveWrap.hidden = false;
          } else {
            saveWrap.hidden = true;
            saveText.textContent = '';
          }
        }
      });
    }

    _updateSellingPlanForm() {
      if (!this._sellingPlanInput) return;
      const variantId = this._getSelectedVariantId();
      const mode = this._getPurchaseType();

      if (
        !this._usesSubscription() ||
        mode === PurchaseMode.ONE_TIME ||
        !this._hasSellingPlansForVariantId(variantId)
      ) {
        this._sellingPlanInput.value = '';
        this._sellingPlanInput.disabled = true;
        return;
      }

      const plan = this._getSelectedPlan();
      this._sellingPlanInput.disabled = false;
      this._sellingPlanInput.value = plan ? String(plan.id) : '';
    }

    _syncSubscription() {
      if (!this._usesSubscription()) return;

      const variantId = this._getSelectedVariantId();

      if (!this._hasSellingPlansForVariantId(variantId)) {
        this._setSubscriptionOptionDisabled(true);
        const oneTime = this.root.querySelector(`${SEL.purchaseInput}[value="one-time"]`);
        if (oneTime) oneTime.checked = true;
        this.root.setAttribute('data-purchase-type', PurchaseMode.ONE_TIME);
      } else {
        this._setSubscriptionOptionDisabled(false);
      }

      if (
        this._getPurchaseType() === PurchaseMode.SUBSCRIPTION &&
        this._hasSellingPlansForVariantId(variantId)
      ) {
        this._rebuildFrequencyOptions(variantId);
      }

      this._updateFrequencyVisibility();
      this._updateFrequencyBenefits();
      this._updateDynamicPrices();
      this._updateSellingPlanForm();
    }

    _handleTabKeyboard(e) {
      const tabs = Array.from(this.root.querySelectorAll(`${SEL.tabBtn}[role="tab"]`));
      if (!tabs.length) return;
      const keys = ['ArrowLeft', 'ArrowRight', 'Home', 'End'];
      if (!keys.includes(e.key)) return;
      const i = tabs.indexOf(e.currentTarget);
      if (i < 0) return;
      e.preventDefault();
      let next = i;
      if (e.key === 'ArrowRight') next = Math.min(i + 1, tabs.length - 1);
      else if (e.key === 'ArrowLeft') next = Math.max(0, i - 1);
      else if (e.key === 'End') next = tabs.length - 1;
      else if (e.key === 'Home') next = 0;
      const tgt = tabs[next];
      tgt?.focus();
      const tab = tgt?.dataset.tab;
      if (tab) this._activateTab(tab, { applyPickSlotDefault: true });
    }

    _handleTabClick(e) {
      const tab = e.currentTarget?.dataset.tab;
      if (tab) this._activateTab(tab, { applyPickSlotDefault: true });
    }

    _activateTab(tab, opts) {
      const applyPickSlotDefault = Boolean(opts?.applyPickSlotDefault);
      const showTabs = this.root.getAttribute('data-show-tabs') === 'true';
      if (!showTabs) return;

      this.root.querySelectorAll(`${SEL.tabBtn}[role="tab"]`).forEach((btn) => {
        const selected = btn.dataset.tab === tab;
        btn.setAttribute('aria-selected', selected ? 'true' : 'false');
        btn.setAttribute('tabindex', selected ? '0' : '-1');
        btn.classList.toggle('is-active', selected);
      });

      this.root.querySelectorAll('[data-tab-panel]').forEach((panel) => {
        const isActive = panel.dataset.tabPanel === tab;
        panel.hidden = !isActive;
        panel.setAttribute('aria-hidden', isActive ? 'false' : 'true');
      });

      this.root.dataset.tabActive = tab;

      if (applyPickSlotDefault && this._config) {
        const panel = this.root.querySelector(`[data-tab-panel="${tab}"]`);
        const radios = panel?.querySelectorAll('input[type="radio"]');
        const next = selectRadioBySlotInPanel(radios, this._config.pickSlotIndex ?? 1);
        if (next && !next.checked) {
          next.checked = true;
          next.dispatchEvent(new Event('change', { bubbles: true }));
          return;
        }
      }

      this._syncSubscription();
    }

    _onRadioChange() {
      this._pushCheckedVariantToForm();
      this._maybeAlignPanelsFromSelection();
      this._refreshSelectionClasses();
      this._syncSubscription();
    }

    _onPurchaseTypeChange() {
      const checked = this.root.querySelector(`${SEL.purchaseInput}:checked`);
      const value = checked ? checked.value : PurchaseMode.ONE_TIME;
      this.root.setAttribute('data-purchase-type', value);
      if (
        value === PurchaseMode.SUBSCRIPTION &&
        this._hasSellingPlansForVariantId(this._getSelectedVariantId())
      ) {
        this._rebuildFrequencyOptions(this._getSelectedVariantId());
      }
      this._syncSubscription();
    }

    _onFrequencyChange() {
      this._updateDynamicPrices();
      this._updateSellingPlanForm();
    }

    _pushCheckedVariantToForm() {
      if (!this._idInput) return;
      const checked = this.root.querySelector(SEL.radioChecked);
      if (!checked) return;
      const vid = checked.value;
      if (String(this._idInput.value) !== String(vid)) {
        this._idInput.value = vid;
        this._idInput.dispatchEvent(new Event('change', { bubbles: true }));
      }
      this._updateSellingPlanForm();
    }

    _maybeAlignPanelsFromSelection() {
      const checked = this.root.querySelector(SEL.radioChecked);
      if (!checked || !this._config || this.root.getAttribute('data-show-tabs') !== 'true') return;
      const grp = Utils.resolvePanelForVariant(this._config, checked.value);
      this._activateTab(grp, { applyPickSlotDefault: false });
    }

    _onIdInputChange() {
      const vid = this._idInput.value;
      if (!vid) return;
      let radioToCheck = null;
      this.root.querySelectorAll(SEL.radioAny).forEach((inp) => {
        if (String(inp.value) === String(vid)) radioToCheck = inp;
      });
      if (radioToCheck && !radioToCheck.checked) {
        radioToCheck.checked = true;
        radioToCheck.dispatchEvent(new Event('change', { bubbles: true }));
      } else if (radioToCheck?.checked) {
        this._maybeAlignPanelsFromSelection();
        this._refreshSelectionClasses();
        this._syncSubscription();
      }
    }

    _refreshSelectionClasses() {
      this.root.querySelectorAll('[data-variant-id]').forEach((row) => {
        const inp = row.querySelector('input[type="radio"]');
        row.classList.toggle('is-selected', Boolean(inp?.checked));
      });
    }
  }

  function bootstrap() {
    document.querySelectorAll(SEL.root).forEach((root) => {
      try {
        const inst = new NwTabVariants(root);
        inst.init();
        Object.defineProperty(root, '_nwTabVariants', { value: inst, enumerable: false, configurable: true });
      } catch (e) {
        console.error('[NwTabVariants]', e);
      }
    });
  }

  window.NwTabVariantsBootstrap = bootstrap;

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', bootstrap);
  } else {
    bootstrap();
  }
})();
